import { useCallback } from 'react'
import clientEnv from '../../../../../src/utils/global/clientEnv'
import {
  type EnterpriseAccountSettingsHiddenSettings,
  type EnterpriseAccountSettingsRsatPulseSurvey,
  RsatPulseSurveySlugEnum,
  useUpdateHiddenSettingsMutation,
} from '../../../../__codegen__/api'
import { useCurrentAccountContext } from '../../../../utils/current-account/CurrentAccountContext'
import { useLocalForageKey } from '../../../../utils/local-storage/useLocalForageKey.hooks'

export const MAX_MODAL_FREQUENCY_COUNT = 2
export const MODAL_FREQUENCY_DISPLAY_MILLISECONDS = 24 * 60 * 60 * 1000
export const SURVEY_END_DATE = new Date('2025-08-09')
export const SURVEY_MILESTONE_SLUG_NAME = RsatPulseSurveySlugEnum.RetailerSatisfactionSurveyR202507

type RetailerFeedbackSurveyStorageProps = {
  surveyLastSeenTime: number
}

const isCypressEnv = () =>
  clientEnv.PUBLIC_CLIENT_CYPRESS_TOKEN && clientEnv.PUBLIC_CLIENT_ENVIRONMENT !== 'production'

export const useRetailerFeedbackSurveyStorage = () => {
  const { data, setData, loading } =
    useLocalForageKey<RetailerFeedbackSurveyStorageProps>('retailerFeedbackSurvey')

  return { data, setData, loading }
}

const getHiddenSettingsForCurrentSlug = (
  hiddenSettings: EnterpriseAccountSettingsHiddenSettings
): EnterpriseAccountSettingsRsatPulseSurvey | null => {
  if (!hiddenSettings?.rsatPulseSurvey?.slug) {
    return null
  }

  if (
    hiddenSettings.rsatPulseSurvey.slug.toLowerCase() === SURVEY_MILESTONE_SLUG_NAME.toLowerCase()
  ) {
    return hiddenSettings.rsatPulseSurvey
  }

  return null
}

export const useRetailerFeedbackSurveyAccess = () => {
  const { data: sessionSurveyData, loading: sessionSurveyDataLoading } =
    useRetailerFeedbackSurveyStorage()
  const currentAccountContext = useCurrentAccountContext()
  const currentAccount = currentAccountContext?.account

  const shouldOpenModal = useCallback(() => {
    /**
     * Skip showing the modal for cypress tests, when the account is not loaded,
     * or when the user is impersonating
     */
    if (
      sessionSurveyDataLoading ||
      isCypressEnv() ||
      !currentAccount ||
      currentAccount.isImpersonating
    ) {
      return false
    }

    const currentTime = Date.now()
    if (currentTime > SURVEY_END_DATE.getTime()) {
      // Only show for specified date range
      return false
    }

    const surveyHiddenSettings = getHiddenSettingsForCurrentSlug(currentAccount.hiddenSettings)

    // Don't prompt more than 2 times or if it's already been submitted
    if (
      surveyHiddenSettings?.hasSubmitted ||
      (surveyHiddenSettings?.seenCount != null &&
        surveyHiddenSettings.seenCount >= MAX_MODAL_FREQUENCY_COUNT)
    ) {
      return false
    }

    const surveyLastSeenTime = surveyHiddenSettings?.lastSeenTime
    if (surveyLastSeenTime != null && isNaN(Date.parse(surveyLastSeenTime))) {
      return false
    }

    // Check the SAVED db data to see if we already prompted in the last 24 hours
    if (
      surveyLastSeenTime != null &&
      currentTime - Date.parse(surveyLastSeenTime) < MODAL_FREQUENCY_DISPLAY_MILLISECONDS
    ) {
      return false
    }

    // Check the BROWSER session data to see if we've already seen the survey in the last 24 hours
    // This is only used to address an edge case where the modal can remain open
    // if the user has multiple tabs open at once
    if (
      sessionSurveyData?.surveyLastSeenTime != null &&
      !isNaN(sessionSurveyData.surveyLastSeenTime) &&
      currentTime - sessionSurveyData.surveyLastSeenTime < MODAL_FREQUENCY_DISPLAY_MILLISECONDS
    ) {
      return false
    }

    return true
  }, [currentAccount, sessionSurveyData, sessionSurveyDataLoading])

  return shouldOpenModal
}

export const useOnRetailerFeedbackSurveyClose = () => {
  const [updateHiddenSettings] = useUpdateHiddenSettingsMutation()
  const { setData: setSessionSurveyData } = useRetailerFeedbackSurveyStorage()

  const onClose = useCallback(() => {
    const surveyLastSeenTime = new Date().getTime()
    updateHiddenSettings({
      variables: {
        hiddenSettings: {
          rsatPulseSurvey: {
            hasSubmitted: false,
            seen: true,
            slug: SURVEY_MILESTONE_SLUG_NAME,
          },
        },
      },
    })
    setSessionSurveyData({
      surveyLastSeenTime,
    })
  }, [setSessionSurveyData, updateHiddenSettings])

  return onClose
}
